import { useState, useEffect, useRef, useCallback } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
    Card,
    CardHeader,
    CardBody,
    FormGroup,
    Label,
    Button,
    Row,
    Col,
    Alert,
    Spinner,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Table,
    Badge,
} from 'reactstrap';
import { toast } from 'react-toastify';
import axios from '../api/axios';
import FeatherIcon from "feather-icons-react";
import StarRatings from 'react-star-ratings';

const MaintenanceFeedbackPage = () => {
    const history = useHistory();
    const { token } = useParams();

    const [ticketData, setTicketData] = useState(null);
    const [workflowData, setWorkflowData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [rating, setRating] = useState(0);
    const [additionalNotes, setAdditionalNotes] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [modal, setModal] = useState(false);
    const [logsModal, setLogsModal] = useState(false);

    const signedUrlCacheRef = useRef(new Map());

    const getSignedUrls = useCallback(async (urls) => {
        if (urls.length === 0) return {};
        const cache = signedUrlCacheRef.current;
        const uncachedUrls = urls.filter(url => {
            const cached = cache.get(url);
            if (!cached) return true;
            const isExpired = Date.now() - cached.timestamp > 50 * 60 * 1000;
            return isExpired;
        });
        if (uncachedUrls.length === 0) {
            const result = {};
            urls.forEach(url => {
                const cached = cache.get(url);
                if (cached) result[url] = cached.signedUrl;
            });
            return result;
        }
        try {
            const response = await axios.post('v2/inventory/consume/get-signed-urls', { urls: uncachedUrls });
            if (response.data.success) {
                const newCache = new Map(cache);
                const result = {};
                response.data.signed_urls.forEach(({ original_url, signed_url }) => {
                    if (signed_url) {
                        newCache.set(original_url, {
                            signedUrl: signed_url,
                            timestamp: Date.now()
                        });
                        result[original_url] = signed_url;
                    }
                });
                urls.forEach(url => {
                    const cached = newCache.get(url);
                    if (cached && !result[url]) result[url] = cached.signedUrl;
                });
                signedUrlCacheRef.current = newCache;
                return result;
            }
        } catch (error) {
            console.error('Error getting signed URLs:', error);
        }
        return {};
    }, []);

    const toggleModal = () => setModal(!modal);
    const toggleLogsModal = () => setLogsModal(!logsModal);

    useEffect(() => {
        if (!token) {
            setError('No feedback token provided.');
            setLoading(false);
            return;
        }
        fetchTicketDetails();
    }, [token]);

    const fetchTicketDetails = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/v2/maintenance/workflow/feedback', {
                params: { token },
            });
            if (response.data.success) {
                setTicketData(response.data.data.ticket);
                setWorkflowData(response.data.data.workflow);
            } else {
                setError(response.data.message || 'Failed to load ticket details.');
            }
        } catch (err) {
            console.error('Error fetching ticket:', err);
            setError(err.response?.data?.message || 'Invalid or expired feedback link.');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        if (rating === 0) {
            toast.error('Please provide a rating.');
            return;
        }

        try {
            setSubmitting(true);
            const response = await axios.post('/v2/maintenance/workflow/feedback', {
                token,
                rating,
                additionalNotes: additionalNotes.trim() || undefined,
            });

            if (response.data.success) {
                toast.success(response.data.message);
                setSubmitted(true);
                const companyDomain = response.data.companyDomain;
                setTimeout(() => {
                    if (companyDomain) {
                        window.location.href = `${companyDomain}`;
                    } else {
                        history.push('/');
                    }
                }, 3000);
            } else {
                setError(response.data.message || 'Failed to submit feedback.');
            }
        } catch (err) {
            console.error('Error submitting feedback:', err);
            setError(err.response?.data?.message || 'Failed to submit feedback.');
        } finally {
            setSubmitting(false);
        }
    };

    const sortedNotificationLogs = workflowData?.notificationLogs ? [...workflowData.notificationLogs].sort((a, b) => new Date(b.sentAt) - new Date(a.sentAt)) : [];

    // Loading state
    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100" style={{ backgroundColor: '#f8f9fa' }}>
                <div className="text-center">
                    <Spinner color="primary" size="lg" />
                    <p className="mt-3 text-muted">Loading feedback form...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: '#f8f9fa', padding: '20px' }}>
                <Card style={{ maxWidth: '500px', width: '100%', border: 'none', boxShadow: '0 4px 6px rgba(0,0,0,0.07)' }}>
                    <CardBody className="text-center p-5">
                        <div style={{
                            width: '80px',
                            height: '80px',
                            borderRadius: '50%',
                            backgroundColor: '#fee',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 24px'
                        }}>
                            <FeatherIcon icon="alert-triangle" size={40} className="text-danger" />
                        </div>
                        <h4 className="mb-3" style={{ fontWeight: '600', color: '#dc3545' }}>Feedback Unavailable</h4>
                        <p className="text-muted mb-4">{error}</p>
                        <Button
                            color="primary"
                            onClick={() => {
                                window.location.href = "https://www.rentdigicare.com/";
                            }}
                            style={{ paddingLeft: "32px", paddingRight: "32px" }}
                        >
                            <FeatherIcon icon="home" size={16} className="me-2" />
                            Back to Home
                        </Button>
                    </CardBody>
                </Card>
            </div>
        );
    }

    // Submitted success state
    if (submitted) {
        return (
            <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ backgroundColor: '#f8f9fa', padding: '20px' }}>
                <Card style={{ maxWidth: '500px', width: '100%', border: 'none', boxShadow: '0 4px 6px rgba(0,0,0,0.07)' }}>
                    <CardBody className="text-center p-5">
                        <div style={{
                            width: '80px',
                            height: '80px',
                            borderRadius: '50%',
                            backgroundColor: '#d4edda',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 24px'
                        }}>
                            <FeatherIcon icon="check-circle" size={40} className="text-success" />
                        </div>
                        <h4 className="mb-3" style={{ fontWeight: '600', color: '#28a745' }}>Thank You!</h4>
                        <p className="text-muted mb-0">Your feedback has been submitted successfully.</p>
                        <p className="text-muted"><small>Redirecting you shortly...</small></p>
                    </CardBody>
                </Card>
            </div>
        );
    }

    const AttachmentPreview = ({ attachment, onMediaClick }) => {
        const [signedUrl, setSignedUrl] = useState(null);
        const [loadState, setLoadState] = useState('loading');

        useEffect(() => {
            const load = async () => {
                try {
                    const urls = await getSignedUrls([attachment.url]);
                    const su = urls[attachment.url] || attachment.url;
                    setSignedUrl(su);
                    setLoadState('ready');
                } catch (error) {
                    console.error('Error loading signed URL:', error);
                    setSignedUrl(attachment.url);
                    setLoadState('ready');
                }
            };
            load();
        }, [attachment.url, getSignedUrls]);

        if (loadState === 'loading') {
            return (
                <div style={{
                    width: '200px',
                    height: '200px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    border: '2px dashed #dee2e6'
                }}>
                    <Spinner size="sm" color="primary" />
                    <small className="ms-2 text-muted">Loading...</small>
                </div>
            );
        }

        const isImage = attachment.type.startsWith('image/');
        const isVideo = attachment.type.startsWith('video/');

        if (isImage || isVideo) {
            const Tag = isImage ? 'img' : 'video';
            return (
                <Tag
                    src={signedUrl}
                    alt={attachment.filename}
                    className="img-fluid rounded"
                    style={{ maxHeight: '200px', objectFit: 'cover', cursor: 'pointer' }}
                    {...(isVideo && { controls: true })}
                    onClick={() => onMediaClick ? onMediaClick(signedUrl) : window.open(signedUrl, '_blank')}
                />
            );
        } else {
            return (
                <a
                    href={signedUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-outline-primary btn-sm"
                >
                    <FeatherIcon icon="download" size={14} className="me-1" />
                    {attachment.filename}
                </a>
            );
        }
    };

    // Main feedback form
    return (
        <div className="min-vh-100" style={{ backgroundColor: '#f8f9fa', padding: '40px 20px' }}>
            <div className="container" style={{ maxWidth: '800px' }}>
                <Card style={{ border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', borderRadius: '12px', overflow: 'hidden' }}>
                    <CardHeader style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        border: 'none',
                        padding: '32px'
                    }}>
                        <div className="d-flex align-items-center">
                            <div style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '12px',
                                backgroundColor: 'rgba(255,255,255,0.2)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginRight: '16px'
                            }}>
                                <FeatherIcon icon="star" size={24} color="white" />
                            </div>
                            <div>
                                <h4 className="mb-0 text-white" style={{ fontWeight: '600' }}>
                                    Provide Feedback
                                </h4>
                                <p className="mb-0 text-white" style={{ opacity: 0.9, fontSize: '14px' }}>
                                    Help us improve our maintenance services
                                </p>
                            </div>
                        </div>
                    </CardHeader>

                    <CardBody style={{ padding: '32px' }}>
                        {/* Ticket Information Card */}
                        <div style={{
                            backgroundColor: '#f8f9fa',
                            borderRadius: '8px',
                            padding: '20px',
                            marginBottom: '32px',
                            border: '1px solid #e9ecef'
                        }}>
                            <div className="d-flex align-items-start mb-3">
                                <FeatherIcon icon="clipboard" size={20} className="text-primary me-2 mt-1" />
                                <div style={{ flex: 1 }}>
                                    <div className="d-flex align-items-center mb-2">
                                        <h5 className="mb-0" style={{ fontWeight: '600', color: '#495057' }}>
                                            Ticket #{ticketData.primaryID}
                                        </h5>
                                        <span style={{
                                            marginLeft: '12px',
                                            padding: '4px 12px',
                                            backgroundColor: '#28a745',
                                            color: 'white',
                                            borderRadius: '12px',
                                            fontSize: '12px',
                                            fontWeight: '600'
                                        }}>
                                            {ticketData.status}
                                        </span>
                                    </div>
                                    <p className="mb-2" style={{ fontSize: '16px', color: '#6c757d', fontWeight: '500' }}>
                                        {ticketData.requestType}
                                    </p>
                                    <p className="mb-2" style={{ fontSize: '14px', color: '#6c757d' }}>
                                        <FeatherIcon icon="home" size={14} className="me-1" />
                                        {ticketData.property} - Suite {ticketData.suite}
                                    </p>
                                    <p className="mb-2" style={{ fontSize: '14px', color: '#6c757d' }}>
                                        <FeatherIcon icon="user" size={14} className="me-1" />
                                        {ticketData.name}
                                    </p>
                                    {ticketData.details && (
                                        <p className="mb-3" style={{ fontSize: '14px', color: '#6c757d', lineHeight: '1.6' }}>
                                            {ticketData.details}
                                        </p>
                                    )}
                                    {ticketData.closureNotes && (
                                        <p className="mb-2" style={{ fontSize: '14px', color: '#28a745', fontWeight: '500' }}>
                                            <FeatherIcon icon="check-circle" size={14} className="me-1" />
                                            Resolution Summary: {ticketData.closureNotes}
                                        </p>
                                    )}
                                    <div className="d-flex flex-wrap gap-2 mb-2">
                                        <Button
                                            color="link"
                                            className="p-0"
                                            onClick={toggleModal}
                                            style={{ fontSize: '14px', textDecoration: 'underline' }}
                                        >
                                            <FeatherIcon icon="eye" size={14} className="me-1" />
                                            View full resolution details
                                        </Button>
                                        <Button
                                            color="link"
                                            className="p-0"
                                            onClick={toggleLogsModal}
                                            style={{ fontSize: '14px', textDecoration: 'underline' }}
                                        >
                                            <FeatherIcon icon="bell" size={14} className="me-1" />
                                            View Notification History
                                        </Button>
                                    </div>
                                    <div style={{ fontSize: '13px', color: '#adb5bd' }}>
                                        <span className="me-3">
                                            <FeatherIcon icon="calendar" size={14} className="me-1" />
                                            Closed: {new Date(ticketData.closedAt).toLocaleDateString('en-US', {
                                                month: 'short',
                                                day: 'numeric',
                                                year: 'numeric'
                                            })}
                                        </span>
                                        {ticketData.timeTaken && (
                                            <span>
                                                <FeatherIcon icon="clock" size={14} className="me-1" />
                                                Time taken: {ticketData.timeTaken} minutes
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Rating Section */}
                        <div style={{ marginBottom: '32px' }}>
                            <div className="text-center" style={{
                                padding: '32px 20px',
                                backgroundColor: '#fff',
                                borderRadius: '8px',
                                border: '2px dashed #e9ecef',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                            }}>
                                <h5 className="mb-4" style={{ fontWeight: '600', color: '#495057' }}>
                                    How would you rate the resolution of your request?
                                </h5>
                                <StarRatings
                                    rating={rating}
                                    starRatedColor="#ffc107"
                                    starHoverColor="#ffc107"
                                    starEmptyColor="#e9ecef"
                                    numberOfStars={5}
                                    name="rating"
                                    starDimension="40px"
                                    starSpacing="8px"
                                    changeRating={(newRating) => setRating(newRating)}
                                />
                                {rating === 0 ? (
                                    <p className="mt-3 mb-0" style={{ fontSize: '14px', color: '#adb5bd' }}>
                                        Click on the stars to rate
                                    </p>
                                ) : (
                                    <div className="mt-3">
                                        <div style={{
                                            display: 'inline-block',
                                            padding: '8px 20px',
                                            backgroundColor: '#d4edda',
                                            borderRadius: '20px'
                                        }}>
                                            <span style={{ fontSize: '16px', color: '#28a745', fontWeight: '600' }}>
                                                {rating} out of 5 stars
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Additional Notes Section */}
                        <FormGroup className="mb-4">
                            <Label className="mb-3" style={{ fontWeight: '600', color: '#495057', fontSize: '15px' }}>
                                <FeatherIcon icon="message-square" size={18} className="me-2" />
                                Additional Comments (Optional)
                            </Label>
                            <textarea
                                className="form-control"
                                rows={5}
                                value={additionalNotes}
                                onChange={(e) => setAdditionalNotes(e.target.value)}
                                placeholder="Share more details about your experience, what went well, or suggestions for improvement..."
                                style={{
                                    borderRadius: '8px',
                                    border: '1px solid #dee2e6',
                                    fontSize: '14px',
                                    resize: 'vertical',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                                }}
                            />
                            <small className="text-muted d-block mt-2">
                                <FeatherIcon icon="info" size={12} className="me-1" />
                                Your feedback helps us improve our maintenance services
                            </small>
                        </FormGroup>

                        {/* Action Buttons */}
                        <div className="d-flex justify-content-between align-items-center" style={{ marginTop: '32px' }}>
                            <Button
                                color="light"
                                onClick={() => history.push('/')}
                                style={{
                                    padding: '10px 24px',
                                    border: '1px solid #dee2e6',
                                    fontWeight: '500'
                                }}
                            >
                                <FeatherIcon icon="arrow-left" size={16} className="me-2" />
                                Back
                            </Button>
                            <Button
                                color="primary"
                                onClick={handleSubmit}
                                disabled={submitting || rating === 0}
                                style={{
                                    padding: '10px 32px',
                                    background: rating === 0 ? '#6c757d' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    border: 'none',
                                    fontWeight: '600',
                                    boxShadow: rating === 0 ? 'none' : '0 4px 12px rgba(102, 126, 234, 0.4)'
                                }}
                            >
                                {submitting ? (
                                    <>
                                        <Spinner size="sm" className="me-2" />
                                        Submitting...
                                    </>
                                ) : (
                                    <>
                                        <FeatherIcon icon="send" size={16} className="me-2" />
                                        Submit Feedback
                                    </>
                                )}
                            </Button>
                        </div>

                        {/* Footer Info */}
                        <div style={{
                            marginTop: '32px',
                            paddingTop: '24px',
                            borderTop: '1px solid #e9ecef'
                        }}>
                            <Row className="text-center">
                                <Col md={6} className="mb-3 mb-md-0">
                                    <div style={{ padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                                        <FeatherIcon icon="shield" size={20} className="text-primary mb-2" />
                                        <p className="mb-0" style={{ fontSize: '13px', color: '#6c757d' }}>
                                            Your feedback is anonymous and secure
                                        </p>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div style={{ padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                                        <FeatherIcon icon="clock" size={20} className="text-primary mb-2" />
                                        <p className="mb-0" style={{ fontSize: '13px', color: '#6c757d' }}>
                                            This feedback link expires in 7 days
                                        </p>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </CardBody>
                </Card>
            </div>

            {/* Resolution Details Modal */}
            <Modal isOpen={modal} toggle={toggleModal} size="lg" centered>
                <ModalHeader toggle={toggleModal} style={{ fontWeight: '600' }}>
                    <FeatherIcon icon="file-text" size={20} className="me-2" />
                    Resolution Details for Ticket #{ticketData?.primaryID}
                </ModalHeader>
                <ModalBody style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                    {workflowData && (
                        <div>
                            {/* Scheduling Section */}
                            <div className="mb-4 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px', borderLeft: '4px solid #007bff' }}>
                                <h6 className="mb-2" style={{ color: '#007bff', fontWeight: '600' }}>
                                    <FeatherIcon icon="calendar" size={16} className="me-1" />
                                    Scheduling
                                </h6>
                                <p className="mb-1"><strong>Date:</strong> {workflowData.scheduling.dateFormatted}</p>
                                <p className="mb-1"><strong>Time Slot:</strong> {workflowData.scheduling.timeSlotFormatted}</p>
                                <p className="mb-0"><strong>Technician:</strong> {workflowData.scheduling.technicianName} ({workflowData.scheduling.technicianPhone})</p>
                                <small className="text-muted">Urgency: {workflowData.scheduling.urgencyLabel}</small>
                            </div>

                            {/* Progress Section */}
                            <div className="mb-4 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px', borderLeft: '4px solid #ffc107' }}>
                                <h6 className="mb-2" style={{ color: '#ffc107', fontWeight: '600' }}>
                                    <FeatherIcon icon="activity" size={16} className="me-1" />
                                    Progress Updates
                                </h6>
                                <p className="mb-0" style={{ fontSize: '14px', lineHeight: '1.5' }}>
                                    {workflowData.progress.notes}
                                </p>
                                {workflowData.progress.expectedArrivalDateFormatted && (
                                    <small className="text-muted d-block mt-1">
                                        Expected Arrival: {workflowData.progress.expectedArrivalDateFormatted}
                                    </small>
                                )}
                            </div>

                            {/* Closure Section */}
                            <div className="mb-4 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px', borderLeft: '4px solid #28a745' }}>
                                <h6 className="mb-2" style={{ color: '#28a745', fontWeight: '600' }}>
                                    <FeatherIcon icon="check-circle" size={16} className="me-1" />
                                    Closure
                                </h6>
                                <p className="mb-2" style={{ fontSize: '14px', lineHeight: '1.5' }}>
                                    {workflowData.closure.notes}
                                </p>
                                <small className="text-muted">Closed on: {workflowData.closure.closedAtFormatted} by {workflowData.closure.closedByName}</small>
                            </div>

                            {/* Attachments Section */}
                            {workflowData.closure.attachments && workflowData.closure.attachments.length > 0 && (
                                <div className="p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px', borderLeft: '4px solid #6f42c1' }}>
                                    <h6 className="mb-2" style={{ color: '#6f42c1', fontWeight: '600' }}>
                                        <FeatherIcon icon="image" size={16} className="me-1" />
                                        Attachments
                                    </h6>
                                    <div className="row">
                                        {workflowData.closure.attachments.map((attachment, index) => (
                                            <div key={index} className="col-12 mb-3">
                                                <AttachmentPreview
                                                    attachment={attachment}
                                                    onMediaClick={(url) => window.open(url, '_blank')}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggleModal}>
                        <FeatherIcon icon="x" size={14} className="me-1" />
                        Close
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Notification Logs Modal */}
            <Modal isOpen={logsModal} toggle={toggleLogsModal} size="xl" centered>
                <ModalHeader toggle={toggleLogsModal} className="border-0 pb-0">
                    <div className="d-flex align-items-center">
                        <div className="bg-primary bg-opacity-10 rounded-circle p-2 me-3">
                            <FeatherIcon icon="bell" size={24} className="text-primary" />
                        </div>
                        <div>
                            <h5 className="mb-1 fw-semibold">Notification History</h5>
                            <small className="text-muted">Ticket #{ticketData?.primaryID}</small>
                        </div>
                    </div>
                </ModalHeader>

                <ModalBody className="pt-3">
                    {sortedNotificationLogs.length > 0 ? (
                        <div className="position-relative">
                            {/* Stats Summary */}
                            <div className="row g-3 mb-4">
                                <div className="col-md-4">
                                    <div className="bg-light rounded-3 p-3 text-center">
                                        <div className="text-muted small mb-1">Total Notifications</div>
                                        <div className="h4 mb-0 fw-bold">{sortedNotificationLogs.length}</div>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="bg-success bg-opacity-10 rounded-3 p-3 text-center">
                                        <div className="text-muted small mb-1">Sent Successfully</div>
                                        <div className="h4 mb-0 fw-bold text-success">
                                            {sortedNotificationLogs.filter(log => log.status === 'sent').length}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="bg-warning bg-opacity-10 rounded-3 p-3 text-center">
                                        <div className="text-muted small mb-1">Pending/Failed</div>
                                        <div className="h4 mb-0 fw-bold text-warning">
                                            {sortedNotificationLogs.filter(log => log.status !== 'sent').length}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Timeline View */}
                            <div style={{ maxHeight: '50vh', overflowY: 'auto' }} className="pe-2">
                                {sortedNotificationLogs.map((log, index) => (
                                    <div
                                        key={log._id || index}
                                        className="border rounded-3 p-3 mb-3 bg-white shadow-sm position-relative"
                                        style={{ transition: 'all 0.2s ease' }}
                                        onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'}
                                        onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)'}
                                    >
                                        {/* Timeline connector */}
                                        {index !== sortedNotificationLogs.length - 1 && (
                                            <div
                                                className="position-absolute bg-light"
                                                style={{
                                                    left: '26px',
                                                    top: '60px',
                                                    width: '2px',
                                                    height: 'calc(100% + 12px)'
                                                }}
                                            />
                                        )}

                                        <div className="d-flex align-items-start">
                                            {/* Status Icon */}
                                            <div
                                                className={`rounded-circle p-2 me-3 flex-shrink-0 ${log.status === 'sent'
                                                    ? 'bg-success bg-opacity-10'
                                                    : 'bg-warning bg-opacity-10'
                                                    }`}
                                                style={{ zIndex: 1, position: 'relative' }}
                                            >
                                                <FeatherIcon
                                                    icon={log.status === 'sent' ? 'check-circle' : 'alert-circle'}
                                                    size={20}
                                                    className={log.status === 'sent' ? 'text-success' : 'text-warning'}
                                                />
                                            </div>

                                            {/* Content */}
                                            <div className="flex-grow-1">
                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                    <div>
                                                        <Badge
                                                            color="primary"
                                                            className="me-2 px-3 py-1"
                                                            style={{ fontSize: '0.8rem', fontWeight: '500' }}
                                                        >
                                                            {log.typeLabel}
                                                        </Badge>
                                                        <Badge
                                                            color={log.status === 'sent' ? 'success' : 'warning'}
                                                            className="px-3 py-1"
                                                            style={{ fontSize: '0.8rem', fontWeight: '500' }}
                                                        >
                                                            <FeatherIcon
                                                                icon={log.status === 'sent' ? 'check' : 'clock'}
                                                                size={12}
                                                                className="me-1"
                                                            />
                                                            {log.status}
                                                        </Badge>
                                                    </div>
                                                    <div className="text-muted small d-flex align-items-center">
                                                        <FeatherIcon icon="clock" size={14} className="me-1" />
                                                        {log.sentAtFormatted}
                                                    </div>
                                                </div>

                                                <p className="mb-2 text-dark" style={{ lineHeight: '1.6' }}>
                                                    {log.message}
                                                </p>

                                                <div className="d-flex align-items-center text-muted small">
                                                    <FeatherIcon icon="user" size={14} className="me-1" />
                                                    <span className="me-2">Sent to:</span>
                                                    <Badge color="light" className="text-dark px-2">
                                                        {log.sentTo}
                                                    </Badge>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-5">
                            <div className="bg-light rounded-circle d-inline-flex p-4 mb-3">
                                <FeatherIcon icon="inbox" size={48} className="text-muted" />
                            </div>
                            <h6 className="text-muted mb-2">No Notification Logs</h6>
                            <p className="text-muted small mb-0">
                                No notifications have been sent for this ticket yet.
                            </p>
                        </div>
                    )}
                </ModalBody>

                <ModalFooter className="border-0 pt-0">
                    <Button
                        color="light"
                        onClick={toggleLogsModal}
                        className="px-4"
                    >
                        <FeatherIcon icon="x" size={14} className="me-2" />
                        Close
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default MaintenanceFeedbackPage;